<template>
  <!-- Neomorph variants use a wrapper + inner structure -->
  <component
    :is="isNeomorph ? 'span' : 'button'"
    v-bind="isNeomorph ? {} : buttonAttrs"
    :class="
      isNeomorph
        ? neomorphWrapperClass
        : [baseClass, sizeClass, variantClass, iconOnlyClass, borderTopClass]
    "
    v-on="isNeomorph ? {} : { click: handleClick }"
  >
    <!-- Neomorph: inner pill -->
    <button
      v-if="isNeomorph"
      v-bind="buttonAttrs"
      :class="[neomorphInnerClass, neomorphInnerSizeClass, neomorphIconOnlyClass]"
      @click="handleClick($event)"
    >
      <p v-if="$slots.icon" :class="iconColorClass">
        <slot name="icon" />
      </p>
      <span v-if="$slots.label" :class="[labelClass, { 'hidden md:inline': isIconOnlyOnMobile }]">
        <slot name="label" />
      </span>
    </button>

    <!-- Flat variants: direct children -->
    <template v-else>
      <p v-if="$slots.icon" :class="iconColorClass">
        <slot name="icon" />
      </p>
      <span v-if="$slots.label" :class="[labelClass, { 'hidden md:inline': isIconOnlyOnMobile }]">
        <slot name="label" />
      </span>
    </template>
  </component>
</template>

<script setup>
import { computed, useSlots } from "vue";

const props = defineProps({
  size: {
    type: String,
    default: "md",
    validator: (value) =>
      ["xs", "sm-xs", "sm", "md-xs", "md-sm", "md", "lg", "lg-sm", "lg-md"].includes(value),
  },
  variant: {
    type: String,
    default: "always-primary",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isIconOnlyOnMobile: {
    type: Boolean,
    default: false,
  },
  interactive: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["click"]);
const slots = useSlots();

const handleClick = (event) => emit("click", event);

const isIconOnly = computed(() => slots.icon && !slots.label);

const neomorphVariants = [
  "always-primary-neo",
  "always-secondary-neo",
  "primary-secondary-neo",
  "primary-grey-neo",
  "paper-primary-neo",
  "paper-grey-neo",
];

const isNeomorph = computed(() => neomorphVariants.includes(props.variant));

const buttonAttrs = computed(() => ({
  disabled: props.disabled,
  type: "button",
}));

const flatInteractive = computed(() => {
  if (!props.interactive) return "";
  return "hover:opacity-85 active:scale-95 active:opacity-100";
});

const flatActiveShadow = computed(() => {
  if (!props.interactive) return "";
  const shadows = {
    "always-primary": "active:shadow-[inset_0_3px_7px_rgba(0,0,0,.18)]",
    "always-secondary": "active:shadow-[inset_0_3px_7px_rgba(0,0,0,.18)]",
    "primary-secondary": "active:shadow-[inset_0_3px_7px_rgba(0,0,0,.18)]",
    "primary-grey": "active:shadow-[inset_0_3px_7px_rgba(0,0,0,.18)]",
    "primary-paper": "active:shadow-[inset_0_3px_7px_rgba(0,0,0,.18)]",
    "always-paper": "active:shadow-[inset_0_3px_7px_rgba(0,0,0,.18)]",
    "always-tertiary": "active:shadow-[inset_0_3px_7px_rgba(0,0,0,.18)]",
    "paper-primary": "active:shadow-[inset_0_3px_7px_rgba(0,0,0,.10)]",
    "paper-grey": "active:shadow-[inset_0_3px_7px_rgba(0,0,0,.10)]",
    "darkpaper-primary": "active:shadow-[inset_0_3px_7px_rgba(0,0,0,.10)]",
  };
  return shadows[props.variant] || "";
});

const flatActiveBorder = computed(() => {
  if (!props.interactive) return "";
  const borders = {
    "always-primary":
      "active:border-cta-secondary-bg-top dark:active:border-cta-secondary-outline-icon",
    "primary-grey":
      "active:border-cta-secondary-bg-top dark:active:border-cta-secondary-outline-icon",
    "always-tertiary": "active:border-primary-4-t",
    "paper-primary": "active:border-cta-tertiary-outline-active",
    "paper-grey": "active:border-cta-tertiary-outline-active",
    "darkpaper-primary": "active:border-cta-tertiary-outline-active",
  };
  return borders[props.variant] || "";
});

const flatActiveGradient = computed(() => {
  if (!props.interactive) return "";
  const gradients = {
    "always-primary": "active:from-primary-6 active:to-primary-4-t",
    "always-secondary": "active:from-secondary-6 active:to-secondary-3",
    "always-paper": "active:from-paper-6 active:to-paper-4",
    "always-tertiary": "active:from-primary-6 active:to-primary-4-t",
    "primary-secondary":
      "active:from-primary-6 active:to-primary-4-t dark:active:from-secondary-6 dark:active:to-secondary-3",
    "primary-grey":
      "active:from-primary-6 active:to-primary-4-t dark:active:from-grey-4 dark:active:to-grey-2",
    "primary-paper":
      "active:from-primary-6 active:to-primary-4-t dark:active:from-paper-6 dark:active:to-paper-4",
    "paper-primary":
      "active:from-paper-6 active:to-paper-4 dark:active:from-primary-6 dark:active:to-primary-4-t",
    "paper-grey":
      "active:from-paper-6 active:to-paper-4 dark:active:from-grey-4 dark:active:to-grey-2",
    "darkpaper-primary":
      "active:from-paper-7 active:to-paper-5 dark:active:from-primary-6 dark:active:to-primary-4-t",
  };
  return gradients[props.variant] || "";
});

const neomorphWrapperInteractive = computed(() => {
  if (!props.interactive) return "";
  return "hover:opacity-90 active:scale-95 active:opacity-100";
});

const neomorphWrapperActiveGradient = computed(() => {
  if (!props.interactive) return "";
  const gradients = {
    "always-primary-neo":
      "active:bg-[radial-gradient(ellipse_at_center_top,#020d1a_0%,#06253f_65%)]",
    "always-secondary-neo":
      "active:bg-[radial-gradient(ellipse_at_center_top,#785400_0%,#daaa00_65%)]",
    "primary-secondary-neo":
      "active:bg-[radial-gradient(ellipse_at_center_top,#020d1a_0%,#06253f_65%)] dark:active:bg-[radial-gradient(ellipse_at_center_top,#785400_0%,#daaa00_65%)]",
    "primary-grey-neo":
      "active:bg-[radial-gradient(ellipse_at_center_top,#020d1a_0%,#06253f_65%)] dark:active:bg-[radial-gradient(ellipse_at_center_top,#595959_0%,#bfbfbf_65%)]",
    "paper-primary-neo":
      "active:bg-[radial-gradient(ellipse_at_center_top,#75613f_0%,#dfc695_65%)]",
    "paper-grey-neo":
      "active:bg-[radial-gradient(ellipse_at_center_top,#75613f_0%,#dfc695_65%)] dark:active:bg-[radial-gradient(ellipse_at_center_top,#595959_0%,#bfbfbf_65%)]",
  };
  return gradients[props.variant] || "";
});

const neomorphInnerActiveShadow = computed(() => {
  if (!props.interactive) return "";
  const shadows = {
    "always-primary-neo": "active:shadow-[inset_0_3px_8px_rgba(0,0,0,.35)]",
    "always-secondary-neo": "active:shadow-[inset_0_3px_8px_rgba(0,0,0,.20)]",
    "primary-secondary-neo":
      "active:shadow-[inset_0_3px_8px_rgba(0,0,0,.35)] dark:active:shadow-[inset_0_3px_8px_rgba(0,0,0,.20)]",
    "primary-grey-neo":
      "active:shadow-[inset_0_3px_8px_rgba(0,0,0,.35)] dark:active:shadow-[inset_0_3px_8px_rgba(0,0,0,.10)]",
    "paper-primary-neo": "active:shadow-[inset_0_3px_8px_rgba(0,0,0,.12)]",
    "paper-grey-neo":
      "active:shadow-[inset_0_3px_8px_rgba(0,0,0,.12)] dark:active:shadow-[inset_0_3px_8px_rgba(0,0,0,.10)]",
  };
  return shadows[props.variant] || "";
});

const neomorphInnerActiveGradient = computed(() => {
  if (!props.interactive) return "";
  const swaps = {
    "always-primary-neo": "active:from-primary-6 active:to-primary-4-t",
    "always-secondary-neo": "active:from-secondary-6 active:to-secondary-3",
    "primary-secondary-neo":
      "active:from-primary-6 active:to-primary-4-t dark:active:from-secondary-6 dark:active:to-secondary-3",
    "primary-grey-neo":
      "active:from-primary-6 active:to-primary-4-t dark:active:from-grey-4 dark:active:to-grey-2",
    "paper-primary-neo": "active:from-paper-6 active:to-paper-4",
    "paper-grey-neo":
      "active:from-paper-6 active:to-paper-4 dark:active:from-grey-4 dark:active:to-grey-2",
  };
  return swaps[props.variant] || "";
});

const baseClass =
  "inline-flex items-center justify-center gap-2 rounded-full font-medium [transition:opacity_0.15s_ease,transform_0.1s_cubic-bezier(0.4,0,0.2,1),box-shadow_0.1s_ease,border-color_0.1s_ease,background_0.1s_ease] cursor-pointer whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-0";

const sizeClass = computed(() => {
  const sizes = {
    xs: "min-h-7 h-7 p-3 text-sm",
    "sm-xs": "min-h-7 h-7 lg:min-h-9 lg:h-9 p-3 lg:p-4 text-sm lg:text-base",
    sm: "min-h-9 h-9 p-4 text-base",
    md: "min-h-11 h-11 p-4 text-base",
    "md-xs": "min-h-7 h-7 p-3 text-sm lg:min-h-11 lg:h-11 lg:p-4 lg:text-base",
    "md-sm": "min-h-9 h-9 p-4 text-base lg:min-h-11 lg:h-11 lg:p-4 lg:text-base",
    "lg-sm": "min-h-9 h-9 lg:min-h-14 lg:h-14 p-4 text-base lg:text-xl",
    "lg-md": "min-h-11 h-11 lg:min-h-14 lg:h-14 p-4 text-base lg:text-xl",
    lg: "min-h-14 h-14 p-4 text-xl",
  };
  return sizes[props.size];
});

const iconOnlyClass = computed(() => {
  if (!isIconOnly.value && !props.isIconOnlyOnMobile) return "";

  const iconOnlySizes = {
    xs: "!min-w-7 !w-7 !px-0",
    "sm-xs": "!min-w-7 !w-7 lg:!min-w-9 lg:!w-9 !px-0 lg:!px-4",
    sm: "!min-w-9 !w-9 !px-0",
    md: "!min-w-11 !w-11 !px-0",
    "md-xs": "!min-w-7 !w-7 !px-0 lg:!min-w-11 lg:!w-11 lg:!px-0",
    "md-sm": "!min-w-9 !w-9 !px-0 lg:!min-w-11 lg:!w-11 lg:!px-0",
    "lg-sm": "!min-w-9 !w-9 !px-0 lg:!min-w-14 lg:!w-14 lg:!px-0",
    "lg-md": "!min-w-11 !w-11 lg:!min-w-14 lg:!w-14 !px-0",
    lg: "!min-w-14 !w-14 !px-0",
  };

  if (props.isIconOnlyOnMobile && !isIconOnly.value) {
    const responsiveSizes = {
      xs: "!min-w-7 !w-7 !px-0 md:!w-auto",
      "sm-xs": "!min-w-7 !w-7 lg:!min-w-9 lg:!w-9 !px-0 md:!w-auto md:!px-4",
      sm: "!min-w-9 !w-9 !px-0 md:!w-auto md:!px-4",
      md: "!min-w-11 !w-11 !px-0 md:!w-auto md:!px-4",
      "md-xs": "!min-w-7 !w-7 !px-0 lg:!min-w-11 lg:!w-11 lg:!px-0 md:!w-auto md:!px-4",
      "md-sm": "!min-w-9 !w-9 !px-0 lg:!min-w-11 lg:!w-11 lg:!px-0 md:!w-auto md:!px-4",
      "lg-sm": "!min-w-9 !w-9 !px-0 lg:!min-w-14 lg:!w-14 lg:!px-0 md:!w-auto md:!px-4",
      "lg-md": "!min-w-11 !w-11 lg:!min-w-14 lg:!w-14 !px-0 md:!w-auto md:!px-4",
      lg: "!min-w-14 !w-14 !px-0 md:!w-auto md:!px-4",
    };
    return responsiveSizes[props.size];
  }

  return iconOnlySizes[props.size];
});

const variantClass = computed(() => {
  if (isNeomorph.value) return "";

  const variants = {
    "always-primary":
      "bg-gradient-to-b from-primary-4-t to-primary-6 text-always-light border-t-2 border-cta-secondary-outline",
    "always-secondary":
      "bg-gradient-to-b from-secondary-3 to-secondary-6 text-grey-10 border-t-2 border-secondary-4",
    "always-paper":
      "bg-gradient-to-b from-paper-4 to-paper-6 text-grey-10 border-t-2 border-paper-6",
    // Tertiary CTA pinned to its dark-mode palette, for the always-dark brand
    // surfaces (the dashboard portfolio panel) that don't flip with the theme.
    "always-tertiary":
      "bg-gradient-to-b from-primary-4-t to-primary-6 text-grey-1 border-t-2 border-primary-3",
    "primary-secondary":
      "bg-gradient-to-b from-primary-4-t to-primary-6 dark:from-secondary-3 dark:to-secondary-6 text-strongest-inverse border-t-2 border-primary-2 dark:border-secondary-4",
    "primary-grey":
      "bg-gradient-to-b from-primary-4-t to-primary-6 dark:from-grey-2 dark:to-grey-4 text-cta-secondary-text border-t-2 border-cta-secondary-outline",
    "primary-paper":
      "bg-gradient-to-b from-primary-4-t to-primary-6 dark:from-paper-4 dark:to-paper-6 text-strongest-inverse border-t-2 border-cta-primary-outline",
    "paper-primary":
      "bg-gradient-to-b from-paper-4 to-paper-6 dark:from-primary-4-t dark:to-primary-6 text-strongest border-t-2 border-cta-tertiary-outline",
    "paper-grey":
      "bg-gradient-to-b from-paper-4 to-paper-6 dark:from-grey-2 dark:to-grey-4 text-grey-10 border-t-2 border-cta-tertiary-outline",
    "darkpaper-primary":
      "bg-gradient-to-b from-paper-5 to-paper-7 dark:from-primary-4-t dark:to-primary-6 text-strongest border-t-2 border-cta-tertiary-outline",
  };

  const base = variants[props.variant] || variants["always-primary"];
  return [
    base,
    flatInteractive.value,
    flatActiveShadow.value,
    flatActiveBorder.value,
    flatActiveGradient.value,
  ]
    .filter(Boolean)
    .join(" ");
});

const borderTopClass = computed(() => {
  if (isNeomorph.value) return "";
  return props.size === "lg" ? "border-t-[3px]" : "";
});

const iconColorClass = computed(() => {
  const iconColors = {
    "always-primary": "text-always-light",
    "always-secondary": "text-grey-10",
    "primary-secondary": "text-strongest-inverse",
    "primary-grey": "text-cta-secondary-text",
    "always-paper": "text-paper-8",
    "always-tertiary": "text-grey-3",
    "paper-primary": "text-cta-tertiary-outline-icon",
    "paper-grey": "text-cta-tertiary-outline-icon",
    "darkpaper-primary": "text-cta-tertiary-outline-icon",
    // Neomorph
    "always-primary-neo": "text-always-light",
    "always-secondary-neo": "text-always-dark",
    "primary-secondary-neo": "text-always-light dark:text-always-dark",
    "primary-grey-neo": "text-always-light dark:text-always-dark",
    "paper-primary-neo": "text-always-dark",
    "paper-grey-neo": "text-always-dark",
  };
  return iconColors[props.variant] || iconColors["always-primary"];
});

const NEO_WRAPPER_NAVY =
  "bg-[radial-gradient(57.97%_100%_at_50%_0%,#9ba8b2_0%,#244057_72.85%,#244057_100%)]";
const NEO_WRAPPER_GOLD =
  "bg-[radial-gradient(57.97%_100%_at_50%_0%,#fff9a3_0%,#ffe852_72.85%,#ffe852_100%)]";
const NEO_WRAPPER_PAPER =
  "bg-[radial-gradient(57.97%_100%_at_50%_0%,#f7f1e6_0%,#f8e5bf_72.85%,#f8e5bf_100%)]";
const NEO_WRAPPER_GOLD_DARK =
  "dark:bg-[radial-gradient(57.97%_100%_at_50%_0%,#fff9a3_0%,#ffe852_72.85%,#ffe852_100%)]";
const NEO_WRAPPER_GREY_DARK =
  "dark:bg-[radial-gradient(57.97%_100%_at_50%_0%,#f0f0f0_0%,#d9d9d9_72.85%,#d9d9d9_100%)]";

const neomorphWrapperClass = computed(() => {
  if (!isNeomorph.value) return "";

  const shared =
    "inline-flex items-start pt-0.5 px-px rounded-full cursor-pointer overflow-hidden origin-center transition-[opacity,transform] duration-100 ease-[cubic-bezier(.4,0,.2,1)]";

  const wrapperBg = {
    "always-primary-neo": NEO_WRAPPER_NAVY,
    "always-secondary-neo": NEO_WRAPPER_GOLD,
    "primary-secondary-neo": `${NEO_WRAPPER_NAVY} ${NEO_WRAPPER_GOLD_DARK}`,
    "primary-grey-neo": `${NEO_WRAPPER_NAVY} ${NEO_WRAPPER_GREY_DARK}`,
    "paper-primary-neo": NEO_WRAPPER_PAPER,
    "paper-grey-neo": `${NEO_WRAPPER_PAPER} ${NEO_WRAPPER_GREY_DARK}`,
  };

  return [
    shared,
    neomorphWrapperInteractive.value,
    wrapperBg[props.variant] || "",
    neomorphWrapperActiveGradient.value,
  ]
    .filter(Boolean)
    .join(" ");
});

const neomorphInnerClass = computed(() => {
  if (!isNeomorph.value) return "";

  const shared =
    "flex-1 min-w-0 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-b transition-[box-shadow,background] duration-100 border-0 cursor-pointer focus:outline-none focus:ring-0";

  const map = {
    "always-primary-neo": ["from-primary-4-t to-primary-6", "text-always-light"],
    "always-secondary-neo": ["from-secondary-3 to-secondary-6", "text-always-dark"],
    "primary-secondary-neo": [
      "from-primary-4-t to-primary-6 dark:from-secondary-3 dark:to-secondary-6",
      "text-always-light dark:text-always-dark",
    ],
    "primary-grey-neo": [
      "from-primary-4-t to-primary-6 dark:from-grey-2 dark:to-grey-4",
      "text-always-light dark:text-always-dark",
    ],
    "paper-primary-neo": ["from-paper-4 to-paper-6", "text-always-dark"],
    "paper-grey-neo": [
      "from-paper-4 to-paper-6 dark:from-grey-2 dark:to-grey-4",
      "text-always-dark",
    ],
  };

  const classes = map[props.variant] || [];
  return [shared, ...classes, neomorphInnerActiveGradient.value, neomorphInnerActiveShadow.value]
    .filter(Boolean)
    .join(" ");
});

const neomorphInnerSizeClass = computed(() => {
  if (!isNeomorph.value) return "";

  const sizes = {
    xs: "h-[35px] px-3 text-sm",
    "sm-xs": "h-[35px] lg:h-[44px] px-3 lg:px-4 text-sm lg:text-base",
    sm: "h-[35px] px-3 text-base",
    md: "h-[44px] px-4 text-base",
    "md-xs": "h-[35px] lg:h-[44px] px-3 lg:px-4 text-sm lg:text-base",
    "lg-sm": "h-[35px] lg:h-[54px] px-3 lg:px-4 text-base lg:text-xl",
    "lg-md": "h-[44px] lg:h-[54px] px-4 text-base lg:text-xl",
    lg: "h-[54px] px-4 text-xl",
  };
  return sizes[props.size];
});

const neomorphIconOnlyClass = computed(() => {
  if (!isNeomorph.value) return "";
  if (!isIconOnly.value && !props.isIconOnlyOnMobile) return "";

  const iconOnlySizes = {
    xs: "!w-[35px] !px-0",
    "sm-xs": "!w-[35px] lg:!w-[44px] !px-0",
    sm: "!w-[35px] !px-0",
    md: "!w-[44px] !px-0",
    "md-xs": "!w-[35px] lg:!w-[44px] !px-0",
    "lg-sm": "!w-[35px] lg:!w-[54px] !px-0",
    "lg-md": "!w-[44px] lg:!w-[54px] !px-0",
    lg: "!w-[54px] !px-0",
  };

  if (props.isIconOnlyOnMobile && !isIconOnly.value) {
    const responsiveSizes = {
      xs: "!w-[35px] !px-0 md:!w-auto md:!px-3",
      "sm-xs": "!w-[35px] lg:!w-[44px] !px-0 md:!w-auto md:!px-3 lg:md:!px-4",
      sm: "!w-[35px] !px-0 md:!w-auto md:!px-3",
      md: "!w-[44px] !px-0 md:!w-auto md:!px-4",
      "md-xs": "!w-[35px] lg:!w-[44px] !px-0 md:!w-auto md:!px-3 lg:md:!px-4",
      "lg-sm": "!w-[35px] lg:!w-[54px] !px-0 md:!w-auto md:!px-3 lg:md:!px-4",
      "lg-md": "!w-[44px] lg:!w-[54px] !px-0 md:!w-auto md:!px-4",
      lg: "!w-[54px] !px-0 md:!w-auto md:!px-4",
    };
    return responsiveSizes[props.size];
  }

  return iconOnlySizes[props.size];
});

const labelClass = computed(() => {
  const base = "whitespace-nowrap leading-normal font-medium";

  if (props.size === "lg") return `${base} font-league`;
  if (props.size === "lg-md" || props.size === "lg-sm") return `${base} font-inter lg:font-league`;
  return `${base} font-inter`;
});
</script>
